import React, {useCallback, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {DefaultButton, Icon, Stack, Text, useTheme} from '@fluentui/react';
import {RootState} from '../../store/rootReducer';
import api, {useEndSupportSessionMutation, useGetSupportSessionStatusQuery} from '../../store/Api';
import {clearSupportAuth} from '../../store/authSlice';
import {clearSupportSession, shortSessionId, SUPPORT_LOGIN_PATH, SUPPORT_SESSION_ENDED} from '../../logic/supportSession';
import SupportSessionNotesPanel, {flushSupportSessionNotes, useSupportNotesOpen} from './SupportSessionNotesPanel';

/**
 * Ends this tab's support session: tells the API (so the key stops working at once, rather than at expiry),
 * forgets it locally, and leaves the tab on the "session ended" page. Never touches the person's own MSAL sign-in.
 */
export const useEndSupportSession = () => {
    const dispatch = useDispatch();
    const [endSession] = useEndSupportSessionMutation();

    return useCallback(async (callApi = true) => {
        if (callApi) {
            // The last few keystrokes of the notes land before the session that may write them is gone.
            await flushSupportSessionNotes();
            try {
                await endSession().unwrap();
            } catch {
                // Already expired or revoked server-side; ending it locally is all that's left.
            }
        }
        clearSupportSession();
        dispatch(clearSupportAuth());
        dispatch(api.util.resetApiState());
        window.location.replace(`${SUPPORT_LOGIN_PATH}?state=${SUPPORT_SESSION_ENDED}`);
    }, [dispatch, endSession]);
};

/**
 * A solid orange-red band across the very top of every screen during a support session, so a support tab can never
 * be mistaken for the person's own: it says the session is logged, who is signed in, and which organization this is.
 * The header is pinned to the window (NavMenu renders it in a Layer), so the band is pinned above it and publishes its
 * height as --support-band-height: the header starts below the band and the page content leaves room for both. The
 * header moves down rather than being covered, and a screenshot for documentation can crop the band off cleanly.
 * Ends the session itself when it expires, since there is no refresh. Its Notes button opens the session's notes
 * (see SupportSessionNotesPanel).
 */
const BAND_HEIGHT_VAR = '--support-band-height';

const SupportSessionBanner: React.FC = () => {
    const theme = useTheme();
    const session = useSelector((state: RootState) => state.auth.supportSession);
    const endSupportSession = useEndSupportSession();
    const band = useRef<HTMLDivElement>(null);
    const [notesOpen, setNotesOpen] = useSupportNotesOpen();
    // Checks in every 30 seconds, so a session ended from the partner portal ends here promptly even on an idle page.
    // A refusal is handled where every 401 is (supportSessionLost in the API base query).
    useGetSupportSessionStatusQuery(undefined, {skip: !session, pollingInterval: 30_000});

    // Keeps the published height in step with the band, which wraps to more lines on narrow windows.
    useEffect(() => {
        const element = band.current;
        const root = document.documentElement;
        if (!element) return;
        const publish = () => root.style.setProperty(BAND_HEIGHT_VAR, `${element.offsetHeight}px`);
        publish();
        const observer = new ResizeObserver(publish);
        observer.observe(element);
        return () => {
            observer.disconnect();
            root.style.removeProperty(BAND_HEIGHT_VAR);
        };
    }, [session?.sessionKey]);

    useEffect(() => {
        if (!session) return;
        const previousTitle = document.title;
        document.title = `[Support] ${session.organizationName ?? session.organization}`;
        const remaining = new Date(session.expiresAt).getTime() - Date.now();
        const timer = window.setTimeout(() => endSupportSession(false), Math.max(remaining, 0));
        return () => {
            window.clearTimeout(timer);
            document.title = previousTitle;
        };
    }, [session?.sessionKey]);

    if (!session) return null;

    const endsAt = new Date(session.expiresAt).toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'});
    const who = session.actorName ? `${session.actorName} (${session.actor})` : session.actor;
    const organization = session.organizationName
        ? `${session.organizationName} (${session.organization})`
        : session.organization;
    // White on Fluent's orange is 4.6:1, just over AA for body text, so nothing on the band is dimmed.
    const onBand = {color: theme.palette.white};
    const bandButton = {
        root: {background: 'transparent', color: theme.palette.white, borderColor: theme.palette.white},
        rootHovered: {background: theme.palette.white, color: theme.palette.orange},
        rootPressed: {background: theme.palette.white, color: theme.palette.orange},
        rootChecked: {background: theme.palette.white, color: theme.palette.orange},
        rootCheckedHovered: {background: theme.palette.white, color: theme.palette.orange},
        icon: {color: 'inherit'},
        iconHovered: {color: theme.palette.orange},
        iconChecked: {color: theme.palette.orange},
    };

    return (
        <>
        <div ref={band} role="region" aria-label="Support session"
             style={{
                 position: 'fixed', top: 0, left: 0, right: 0,
                 // Under Fluent layers (1000000): the header layer is offset below the band, and panels and dialogs
                 // may cover it while they are open.
                 zIndex: 999999,
                 boxSizing: 'border-box',
                 background: theme.palette.orange, color: theme.palette.white, padding: '0.5em 1em',
             }}>
            <Stack horizontal wrap verticalAlign="center" horizontalAlign="space-between" tokens={{childrenGap: '0.5em 2em'}}>
                <Stack horizontal verticalAlign="center" tokens={{childrenGap: '0.5em'}}>
                    <Icon iconName="Shield" styles={{root: {fontSize: 20}}}/>
                    <Text variant="mediumPlus" styles={{root: {...onBand, fontWeight: 700}}}>
                        Logged support session
                    </Text>
                </Stack>
                <Stack horizontal wrap verticalAlign="center" tokens={{childrenGap: '0.5em 2em'}}>
                    <Text styles={{root: onBand}}><span>Signed in as </span><strong>{who}</strong></Text>
                    <Text styles={{root: onBand}}><span>Organization </span><strong>{organization}</strong></Text>
                    <Text styles={{root: onBand}}><span>Ends at </span><strong>{endsAt}</strong></Text>
                    {session.sessionId && (
                        <Text styles={{root: onBand}} title={`Session ${session.sessionId}`}>
                            <span>Session </span><strong style={{fontFamily: 'monospace'}}>{shortSessionId(session.sessionId)}</strong>
                        </Text>
                    )}
                    <DefaultButton text="Notes" iconProps={{iconName: 'EditNote'}} toggle checked={notesOpen}
                                   aria-expanded={notesOpen} onClick={() => setNotesOpen(!notesOpen)} styles={bandButton}/>
                    <DefaultButton text="End session" iconProps={{iconName: 'SignOut'}} onClick={() => endSupportSession()}
                                   styles={bandButton}/>
                </Stack>
            </Stack>
        </div>
        <SupportSessionNotesPanel isOpen={notesOpen} onDismiss={() => setNotesOpen(false)}/>
        </>
    );
};

export default SupportSessionBanner;
